import { Download, FileText } from "lucide-react";
import type { ReactNode } from "react";
import { useI18n, useSlotText } from "@/i18n/translations";
import type { Slot } from "@/i18n/translations";
import { Reveal } from "@/components/Reveal";

/**
 * Cornice passe-partout standard per le fotografie del sito (con didascalia «foto dimostrativa»).
 * Stessa famiglia della foto della Home: bordo ink/40, passe-partout p-2/p-3, filetto interno ink/25.
 * `fill`: la foto riempie l'altezza della colonna che la contiene (intestazioni di pagina) —
 * non impone altezza propria, quindi non spinge giù i testi accanto.
 */
export const FotoSlot = ({
  src,
  captionKey,
  testid,
  ratio = "aspect-[3/2]",
  className = "",
  fill = false,
}: {
  src: string;
  captionKey: string;
  testid: string;
  ratio?: string;
  className?: string;
  fill?: boolean;
}) => {
  const { t } = useI18n();
  return (
    <figure className={`${fill ? "flex h-full flex-col" : ""} ${className}`} data-testid={testid}>
      <div className={`border border-ink/40 bg-cardwhite p-2 sm:p-3 ${fill ? "flex flex-1 flex-col" : ""}`}>
        <div className={`relative overflow-hidden ${fill ? `flex-1 ${ratio}` : ""}`}>
          <img
            src={src}
            alt={t(captionKey)}
            loading="lazy"
            style={{ filter: "saturate(0.5) contrast(0.85) brightness(1.12)" }}
            className={fill ? "img-treated absolute inset-0 h-full w-full object-cover" : `img-treated w-full object-cover ${ratio}`}
          />
          <div className="pointer-events-none absolute inset-0 bg-paper/35" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 border border-ink/25" aria-hidden="true" />
        </div>
      </div>
      <figcaption className="mt-2.5 text-xs italic leading-relaxed text-mutedtx">
        {t("foto.dimostrativa")} — {t(captionKey)}
      </figcaption>
    </figure>
  );
};

/** Badge visibile su ogni slot testuale provvisorio nostro. */
export const DemoBadge = ({ className = "", label }: { className?: string; label?: string }) => {
  const { t } = useI18n();
  return (
    <span
      data-testid="demo-placeholder-badge"
      className={`inline-flex items-center gap-1.5 border border-amber-500/50 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-800 ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
      {label ?? t("common.demo")}
    </span>
  );
};

/** Testo di uno slot: in EN, se la traduzione manca, avvisa e mostra l'italiano. */
export const SlotText = ({
  slot,
  className = "",
}: {
  slot: Slot;
  className?: string;
}) => {
  const { lang, t } = useI18n();
  const { text, pending } = useSlotText()(slot);
  return (
    <span className={className}>
      {pending && (
        <em className="mb-3 block border-l-2 border-amberv/50 pl-3 text-sm italic text-mutedtx">
          {t("common.en_pending")}
        </em>
      )}
      {text}
    </span>
  );
};

/** Bottone PDF segnaposto — nessun file inventato: il download parte solo con il documento reale. */
export const PdfButton = ({ label }: { label: string }) => {
  const { t } = useI18n();
  return (
    <div data-testid="pdf-download-placeholder" className="flex flex-col items-start gap-1">
      <button
        type="button"
        disabled
        title={t("common.pdf_note")}
        className="inline-flex cursor-not-allowed items-center gap-2 border border-ink/20 bg-mutedbg px-5 py-3 text-sm font-semibold text-mutedtx"
      >
        {label}
        <Download className="h-4 w-4" aria-hidden="true" />
        <span className="text-xs font-medium uppercase tracking-wider">{t("common.pdf_pending")}</span>
      </button>
      <span className="text-xs text-mutedtx">{t("common.pdf_note")}</span>
    </div>
  );
};

export const PdfIcon = () => <FileText className="h-4 w-4 text-bordeaux" aria-hidden="true" />;

/** Link a documento PDF (locandine, programmi, atti). Le etichette note vengono localizzate. */
const ETICHETTE_PDF_EN: Record<string, string> = {
  "Apri il programma (PDF)": "Open the programme (PDF)",
  "Apri la locandina (PDF)": "Open the poster (PDF)",
};

export const localizedPdfLabel = (label: string, lang: string) =>
  lang === "en" ? ETICHETTE_PDF_EN[label] ?? label : label;

export const PdfLinkRow = ({ label, href }: { label: string; href: string }) => {
  const { lang } = useI18n();
  const shown = localizedPdfLabel(label, lang);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="link-underline inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux">
      <Download className="h-4 w-4" aria-hidden="true" />
      {shown}
    </a>
  );
};

/** Nota redazionale — regole di pubblicazione e fonti. */
export const NoteCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <aside className="border-l-2 border-bordeaux bg-mutedbg/70 p-5">
    <span className="eyebrow mb-2 block text-bordeaux">{title}</span>
    <div className="text-sm leading-relaxed text-bodytx/80">{children}</div>
  </aside>
);

/**
 * Intestazione di pagina interna — stessa griglia dell'hero della Home (12 colonne, testo 7 / foto 5).
 * La foto (aside) sta a fianco del titolo, allineata in alto al kicker, ferma (nessuna animazione)
 * e si allunga fino al fondo del blocco testo. `asideWide`: testo breve (es. Contatti) → foto 7 colonne.
 */
export const PageHeader = ({
  kickerKey,
  titleKey,
  aside,
  asideWide = false,
  children,
}: {
  kickerKey: string;
  titleKey: string;
  aside?: ReactNode;
  asideWide?: boolean;
  children?: ReactNode;
}) => {
  const { t } = useI18n();
  const text = (
    <>
      <p className="eyebrow mb-4 flex items-center gap-3 text-bordeaux">
        <span className="inline-block h-px w-10 bg-bordeaux" aria-hidden="true" />
        {t(kickerKey)}
      </p>
      <h1 className="max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
        {t(titleKey)}
      </h1>
      {children}
    </>
  );
  return (
    <header className="border-b border-borderc bg-cardwhite">
      <div className="container-x py-14 sm:py-20">
        {aside ? (
          <div className="grid gap-10 sm:grid-cols-12 sm:items-stretch sm:gap-8 lg:gap-12">
            <Reveal className={`min-w-0 ${asideWide ? "sm:col-span-5" : "sm:col-span-7"}`}>{text}</Reveal>
            <div className={`min-w-0 ${asideWide ? "sm:col-span-7" : "sm:col-span-5"}`}>{aside}</div>
          </div>
        ) : (
          <Reveal>{text}</Reveal>
        )}
      </div>
    </header>
  );
};
